/**
 * PLAT-85 — pure logic of the shared one-time-code input (one-time-code-input.vue).
 * Free of Vue/DOM so it can be unit-tested under node vitest.
 */
export const CODE_LENGTH = 6;

const digitsOf = (raw) => String(raw === null || raw === undefined ? '' : raw).replace(/[^0-9]/g, '');

/** ASCII digits only (Unicode digits are not accepted by the server either), cut to `length`. */
export function sanitizeCode(raw, length = CODE_LENGTH) {
    return digitsOf(raw).slice(0, length);
}

/**
 * Digits of an inserted text, most specific first: (1) the first run of exactly `length` digits bounded by non-digits
 * ("Order 20240101 code 123456" -> 123456, not 202401); (2) the first run of `length` or more digits
 * ("Ref 5: 123456" -> 123456, not 512345; "1234567" -> 123456); (3) every digit in order ("123 456" -> 123456).
 */
const insertedDigitsOf = (insertedData, length) => {
    const text = String(insertedData === null || insertedData === undefined ? '' : insertedData);
    // No lookbehind: unsupported before Safari/iOS 16.4.
    const exact = text.match(new RegExp(`(?:^|[^0-9])([0-9]{${length}})(?![0-9])`));
    if (exact) return exact[1];
    const run = text.match(new RegExp(`[0-9]{${length},}`));
    return run ? run[0] : digitsOf(text);
};

/**
 * The next field value after an input event. A whole-code insertion (keyboard suggestion, autofill, paste) of `length`
 * or more digits replaces the field even when boxes are already filled; anything else is the field's own value
 * (typing, deleting) cleaned of non-digits.
 */
export function nextCode(prev, rawValue, insertedData, length = CODE_LENGTH) {
    const inserted = insertedDigitsOf(insertedData, length);
    if (inserted.length >= length) return inserted.slice(0, length);
    return sanitizeCode(rawValue, length);
}

/** Index of the box with the focus ring: the next empty one, or the last when full. -1 when not focused. */
export function activeBoxIndex(value, focused, length = CODE_LENGTH) {
    if (!focused) return -1;
    return Math.min(String(value || '').length, length - 1);
}

export function isCompleteCode(value, length = CODE_LENGTH) {
    return new RegExp(`^[0-9]{${length}}$`).test(String(value || ''));
}
